import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Beaker, FlaskConical, ShieldCheck, Users, MapPin, Sparkles, Flag, Target, Heart, ArrowRight, Truck, CreditCard } from "lucide-react";
import Seo from "@/components/common/Seo";
import Reveal from "@/components/common/Reveal";
import { TrustedMarquee } from "@/components/layout/TrustedMarquee";
import { stagger, fadeUp } from "@/lib/motion";

const PILLARS = [
  { icon: Beaker, title: "Premium Ingredients", text: "Only the finest raw materials make it into our formulas." },
  { icon: FlaskConical, title: "Advanced Formulations", text: "Science-led recipes engineered for real results." },
  { icon: ShieldCheck, title: "Tested for Performance", text: "Validated in real-world driving conditions." },
  { icon: Users, title: "Trusted by 10,000+", text: "A growing community of happy car owners." },
  { icon: MapPin, title: "Proudly Made in India", text: "Engineered and manufactured with pride." },
];

const STORY = [
  { tag: "Our Beginning", title: "Born from passion", text: "AutoCareX started with a simple obsession — the perfect finish. Frustrated by inconsistent products, we set out to build car care that actually performs." },
  { tag: "Our Growth", title: "From garage to brand", text: "What began with a handful of formulas became a full range trusted by detailers and everyday drivers across the country." },
  { tag: "Our Future", title: "Innovating forward", text: "We continue to push formulation science, sustainability and design to redefine what intelligent car care means." },
];

const DIFFERENT = [
  { icon: FlaskConical, title: "Advanced Formulations" },
  { icon: ShieldCheck, title: "Safe & Effective" },
  { icon: Target, title: "Rigorously Tested" },
  { icon: Sparkles, title: "Consistent Quality" },
  { icon: Flag, title: "Made for India" },
  { icon: Heart, title: "Passion First" },
];

export default function About() {
  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo title="About Us" description="AutoCareX — engineered for excellence, driven by passion. Premium ingredients, advanced formulations, proudly made in India." />

      {/* Hero */}
      <section className="relative acx-noise overflow-hidden border-b border-[color:var(--acx-border)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_circle_at_30%_0%,rgba(156,163,175,0.10),transparent_55%),radial-gradient(700px_circle_at_80%_10%,rgba(225,27,34,0.10),transparent_55%)]" />
        <div className="acx-container relative px-4 py-20 text-center sm:px-6 lg:px-10 lg:py-32">
          <Reveal>
            <span className="text-[12px] uppercase tracking-[0.22em] text-white/50">About AutoCareX</span>
            <h1 className="mx-auto mt-4 max-w-4xl font-display text-[40px] font-extrabold leading-[1.04] tracking-[-0.03em] sm:text-6xl lg:text-7xl">
              Engineered for Excellence.<br /><span className="text-[#E11B22]">Driven by Passion.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-[17px] leading-relaxed text-white/65">
              We build intelligent car care products that protect, restore and elevate every drive —
              combining premium ingredients with advanced formulation science.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-b border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
        <div className="acx-container px-4 py-16 sm:px-6 lg:px-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {PILLARS.map((p) => (
              <motion.div key={p.title} variants={fadeUp} className="rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-5 text-center">
                <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/5 text-white/80"><p.icon size={20} /></span>
                <p className="mt-3 text-[14px] font-semibold text-white">{p.title}</p>
                <p className="mt-1.5 text-[12px] leading-relaxed text-white/50">{p.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="border-b border-[color:var(--acx-border)] bg-[var(--acx-bg)]">
        <div className="acx-container grid grid-cols-1 gap-6 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <div className="h-full rounded-[20px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-8">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#E11B22]/15 text-[#E11B22]"><Target size={20} /></span>
              <h3 className="mt-5 font-display text-2xl font-bold">Our Mission</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-white/60">
                To make professional-grade car care accessible to everyone — delivering products that
                are effective, safe and a pleasure to use, so every drive feels like new.
              </p>
            </div>
          </Reveal>
          <Reveal>
            <div className="h-full rounded-[20px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-8">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#9CA3AF]/15 text-[#9CA3AF]"><Sparkles size={20} /></span>
              <h3 className="mt-5 font-display text-2xl font-bold">Our Vision</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-white/60">
                To become India's most trusted and innovative car care brand — setting the standard for
                intelligent, high-performance automotive care worldwide.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Story timeline */}
      <section className="border-b border-[color:var(--acx-border)] bg-[var(--acx-bg-2)]">
        <div className="acx-container px-4 py-20 sm:px-6 lg:px-10">
          <Reveal className="mb-12">
            <span className="text-[12px] uppercase tracking-[0.22em] text-white/50">Our Story</span>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl">From Enthusiasts to Innovators</h2>
          </Reveal>
          <div className="relative grid grid-cols-1 gap-8 md:grid-cols-3">
            {STORY.map((s, i) => (
              <Reveal key={s.tag} delay={i * 0.08}>
                <div className="relative rounded-[18px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-7">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#E11B22] text-[14px] font-bold text-white">{i + 1}</span>
                  <p className="mt-4 text-[12px] uppercase tracking-[0.18em] text-white/45">{s.tag}</p>
                  <h3 className="mt-1 font-display text-xl font-bold text-white">{s.title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-white/60">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted by marquee */}
      <TrustedMarquee />

      {/* What makes different */}
      <section className="border-b border-[color:var(--acx-border)] bg-[var(--acx-bg)]">
        <div className="acx-container px-4 py-20 sm:px-6 lg:px-10">
          <Reveal className="mb-10 text-center">
            <h2 className="font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl">What makes AutoCareX different?</h2>
          </Reveal>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger} className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {DIFFERENT.map((d) => (
              <motion.div key={d.title} variants={fadeUp} className="flex flex-col items-center rounded-[16px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-5 text-center">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/5 text-white/80"><d.icon size={19} /></span>
                <p className="mt-3 text-[13px] font-semibold text-white">{d.title}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative acx-noise overflow-hidden bg-[var(--acx-bg-2)]">
        <div className="acx-container px-4 py-20 text-center sm:px-6 lg:px-10">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-display text-4xl font-bold tracking-[-0.02em] sm:text-5xl">
              Let's keep your passion looking its best.
            </h2>
            <Link to="/shop" className="mt-8 inline-flex items-center gap-2 rounded-[12px] bg-white px-7 py-4 text-[15px] font-semibold text-black transition-colors hover:bg-white/90">
              Shop Our Products <ArrowRight size={17} />
            </Link>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
              <div className="flex items-center gap-2 text-white/60"><Sparkles size={18} /><span className="text-[13px]">Premium Quality</span></div>
              <div className="flex items-center gap-2 text-white/60"><Truck size={18} /><span className="text-[13px]">Fast Delivery</span></div>
              <div className="flex items-center gap-2 text-white/60"><CreditCard size={18} /><span className="text-[13px]">Secure Payment</span></div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
